import { getAuth, signInWithCustomToken } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from '../firebase/config';
import { FUNCTIONS_REGION } from '../firebase/region';
import { secretFrom, usableKeys, type GuestKeys } from './guestIdentity';

/**
 * Signing in as a guest, in a browser — the web twin of guest.ts.
 *
 * A browser has no device id to ask for, and this product will not fingerprint
 * a reader to invent one. So the id is simply generated on first use and kept
 * beside the secret, which makes the promise weaker and honest: clearing site
 * data ends that guest, and the screen says so.
 *
 * localStorage rather than a cookie: nothing here is sent to a server on its
 * own, and a cookie would be.
 */

const KEY = 'fusilat.guest.keys';

const fns = () => getFunctions(app, FUNCTIONS_REGION);

function randomHex(bytes: number): string {
  const buffer = new Uint8Array(bytes);
  crypto.getRandomValues(buffer);
  return secretFrom(buffer);
}

export async function guestKeys(): Promise<GuestKeys> {
  try {
    const held = window.localStorage.getItem(KEY);
    const parsed = held ? (JSON.parse(held) as Partial<GuestKeys>) : null;
    if (usableKeys(parsed)) return parsed;
  } catch {
    // A private window refuses storage outright; that is the same as empty.
  }

  const keys: GuestKeys = { deviceId: randomHex(16), deviceSecret: randomHex(32) };
  try {
    window.localStorage.setItem(KEY, JSON.stringify(keys));
  } catch {
    /*
     * A browser that will not keep the pair still gets a session — the reader
     * simply arrives as a new guest next time. "No app" is a worse answer than
     * "an app that forgets" for somebody who wanted to try it.
     */
  }
  return keys;
}

export async function signInAsGuest(): Promise<{ returning: boolean }> {
  const keys = await guestKeys();
  const call = httpsCallable<GuestKeys, { token: string; returning: boolean }>(
    fns(), 'guestSignIn',
  );
  const { data } = await call(keys);
  await signInWithCustomToken(getAuth(app), data.token);
  return { returning: data.returning };
}

export async function forgetGuest(): Promise<void> {
  try {
    window.localStorage.removeItem(KEY);
  } catch {
    // Nothing to forget is the wanted end state anyway.
  }
}

/**
 * Keeping a guest's history by putting a phone on it.
 *
 * The uid does not change — the server attaches the number to the account
 * that already exists — so coverage, marks, certificates and points simply
 * stay theirs. The device id goes along so the server can retire this
 * device's key: from then on the account is entered by phone, and holding
 * the handset is no longer enough.
 */
export async function claimGuestAccount(phone: string, code: string): Promise<void> {
  const keys = await guestKeys();
  const call = httpsCallable<
    { phone: string; code: string; deviceId: string },
    { claimed: boolean }
  >(fns(), 'claimGuestAccount');
  await call({ phone, code, deviceId: keys.deviceId });
  await forgetGuest();
}
